(function orbitalP5Sketch() {
  /* ── Orbit shape & motion ─────────────────────────────── */
  const DEFAULT_ORBIT = {
    imageCount: 24, // number of images placed around the ring
    revolutionMs: 35000, // ms for one full auto-rotation
    radiusScale: 0.33, // base radius as fraction of smallest viewport side
    maxTiltDegrees: 90, // portrait uprightness (90 = vertical, 0 = most tilted)
    stretchWide: 2, // horizontal ellipse multiplier in landscape
    stretchNarrow: 0.66, // compressed axis multiplier (opposite of wide)
    spacingScale: 1.16, // uniform scale applied after stretch (controls gap)
    edgePadding: 8, // px inset from canvas edge for fit calculations
  };

  /* ── Image appearance ────────────────────────────────── */
  const DEFAULT_IMAGES = {
    cornerRadius: 12, // rounded-rect corner radius for each image tile (irrelevent if transparent img)
    pixelDensity: 2, // img resolution multiplier (higher = sharper but more GPU memory usage)
    sizeLandscape: 180, // image size in px at full landscape
    sizePortrait: 120, // image size in px at full portrait
  };

  /* ── Depth & lighting ────────────────────────────────── */
  const DEFAULT_DEPTH = {
    enabled: true, // toggle depth-based size scaling on/off
    scaleMin: 0.75, // size multiplier for farthest-back images
    scaleMax: 1.2, // size multiplier for closest-front images
    gamma: 1.4, // curve exponent for brightness falloff
    brightnessMin: 120, // tint value for back images (0-255)
    brightnessMax: 255, // tint value for front images (0-255)
  };

  /* ── Hover effect ────────────────────────────────────── */
  const DEFAULT_HOVER = {
    brightnessBoost: 20, // back/middle image brightness
    scale: 1.12, // scale-up factor on hover
    lerpSpeed: 0.15, // smoothing speed for hover animation (0-1)
    brakeLerpSpeed: 0.04, // how fast auto-rotation decelerates/recovers on hover (0-1)
  };

  /* ── Start reveal sequence ─────────────────────────────── */
  const REVEAL = {
    staggerMs: 80, // delay between each successive orbit slot appearing
    fadeMs: 400, // fade-in duration per slot (ms)
  };

  /* ── Drag & physics ──────────────────────────────────── */
  const INTERACTION = {
    rotationLerpSpeed: 0.08, // smoothing speed for tilt transition (0-1)
    velocityFriction: 0.95, // per-frame velocity decay after fling (0-1)
    velocityThreshold: 0.000001, // velocity below this snaps to zero
    dragSensitivity: 0.002, // angle change per px of drag movement
    flingSpeed: 0.004, // velocity multiplier from last drag gesture
    dragHorizontalWeight: 0.7, // how much horizontal drag contributes
    dragVerticalWeight: 0.3, // how much vertical drag contributes
    minFrameMs: 16, // fallback delta-time floor (~60 fps)
    touchLockThreshold: 10, // px of movement before locking touch direction
    clickMaxDisplacement: 5, // px threshold to distinguish click from drag
  };

  /* ── Responsive behaviour ────────────────────────────── */
  const RESPONSIVE = {
    portraitAspect: 0.6, // aspect ratio below which layout is fully portrait
    landscapeAspect: 1.6, // aspect ratio above which layout is fully landscape
    shortSideMin: 420, // viewport short-side where small-screen tuning starts
    shortSideMax: 1080, // viewport short-side where tuning reaches full strength
    stretchDampMin: 0.84, // stretch reduction floor on small viewports (0-1)
    imageMaxRatio: 0.2, // max image size as fraction of short viewport side
    imageMin: 84, // absolute minimum image size in px
    portraitRadiusBlend: 0.55, // how much screen height boosts radius in portrait
    widthOverflow: 1.65, // allowed horizontal overflow on small screens (1 = none)
  };

  const $script = document.currentScript;
  if (!$script) return;

  const sectionId = $script.getAttribute('data-section-id');
  if (!sectionId) return;

  const $section = document.getElementById(sectionId);
  if (!$section) return;

  const $canvas = $section.querySelector('[data-orbital-p5-canvas]');
  if (!$canvas) return;

  const $urls = $section.querySelector('[data-orbital-p5-urls]');
  if (!$urls) return;
  const $config = $section.querySelector('[data-orbital-p5-config]');

  let imageUrls;
  try {
    imageUrls = JSON.parse($urls.textContent);
  } catch (_) {
    return;
  }

  if (!Array.isArray(imageUrls)) return;

  let sectionConfig = {};
  if ($config) {
    try {
      sectionConfig = JSON.parse($config.textContent);
    } catch (_) {
      sectionConfig = {};
    }
  }

  const $productUrls = $section.querySelector('[data-orbital-p5-product-urls]');
  let productUrls = [];
  if ($productUrls) {
    try {
      const parsed = JSON.parse($productUrls.textContent);
      if (Array.isArray(parsed)) productUrls = parsed;
    } catch (_) {
      productUrls = [];
    }
  }

  const filteredImages = [];
  const filteredProducts = [];
  for (let i = 0; i < imageUrls.length; i++) {
    if (typeof imageUrls[i] === 'string' && imageUrls[i].trim()) {
      filteredImages.push(imageUrls[i]);
      filteredProducts.push(productUrls[i] != null ? productUrls[i] : null);
    }
  }
  imageUrls = filteredImages;
  productUrls = filteredProducts;
  if (imageUrls.length === 0) return;

  const hasAnyProductUrl = productUrls.some(function hasUrl(url) {
    return url != null;
  });

  function toNumber(value, fallback) {
    if (value === null || value === undefined || value === '') return fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function toBoolean(value, fallback) {
    if (typeof value === 'boolean') return value;
    return fallback;
  }

  const orbitConfig = sectionConfig.orbit || {};
  const depthConfig = sectionConfig.depth || {};
  const hoverConfig = sectionConfig.hover || {};
  const imageConfig = sectionConfig.images || {};

  const ORBIT = {
    ...DEFAULT_ORBIT,
    imageCount: clamp(
      Math.round(toNumber(orbitConfig.imageCount, DEFAULT_ORBIT.imageCount)),
      16,
      24,
    ),
    revolutionMs: clamp(
      toNumber(orbitConfig.revolutionMs, DEFAULT_ORBIT.revolutionMs),
      5000,
      120000,
    ),
    stretchWide: clamp(
      toNumber(orbitConfig.stretchWide, DEFAULT_ORBIT.stretchWide),
      1,
      3,
    ),
    stretchNarrow: clamp(
      toNumber(orbitConfig.stretchNarrow, DEFAULT_ORBIT.stretchNarrow),
      0.2,
      1,
    ),
    spacingScale: clamp(
      toNumber(orbitConfig.spacingScale, DEFAULT_ORBIT.spacingScale),
      0.6,
      2,
    ),
    radiusScale: clamp(
      toNumber(orbitConfig.radiusScale, DEFAULT_ORBIT.radiusScale),
      0.1,
      0.8,
    ),
    maxTiltDegrees: clamp(
      toNumber(orbitConfig.maxTiltDegrees, DEFAULT_ORBIT.maxTiltDegrees),
      0,
      90,
    ),
  };

  const DEPTH = {
    ...DEFAULT_DEPTH,
    enabled: toBoolean(depthConfig.enabled, DEFAULT_DEPTH.enabled),
    scaleMin: clamp(
      toNumber(depthConfig.scaleMin, DEFAULT_DEPTH.scaleMin),
      0.3,
      1.2,
    ),
    scaleMax: clamp(
      toNumber(depthConfig.scaleMax, DEFAULT_DEPTH.scaleMax),
      0.8,
      2,
    ),
    gamma: clamp(toNumber(depthConfig.gamma, DEFAULT_DEPTH.gamma), 0.3, 3),
    brightnessMin: clamp(
      toNumber(depthConfig.brightnessMin, DEFAULT_DEPTH.brightnessMin),
      0,
      255,
    ),
    brightnessMax: clamp(
      toNumber(depthConfig.brightnessMax, DEFAULT_DEPTH.brightnessMax),
      0,
      255,
    ),
  };

  if (DEPTH.scaleMin > DEPTH.scaleMax) {
    const tmp = DEPTH.scaleMin;
    DEPTH.scaleMin = DEPTH.scaleMax;
    DEPTH.scaleMax = tmp;
  }
  if (DEPTH.brightnessMin > DEPTH.brightnessMax) {
    const tmp = DEPTH.brightnessMin;
    DEPTH.brightnessMin = DEPTH.brightnessMax;
    DEPTH.brightnessMax = tmp;
  }

  const HOVER = {
    ...DEFAULT_HOVER,
    brightnessBoost: clamp(
      toNumber(hoverConfig.brightnessBoost, DEFAULT_HOVER.brightnessBoost),
      0,
      80,
    ),
    scale: clamp(toNumber(hoverConfig.scale, DEFAULT_HOVER.scale), 1, 1.5),
    lerpSpeed: clamp(
      toNumber(hoverConfig.lerpSpeed, DEFAULT_HOVER.lerpSpeed),
      0.01,
      0.4,
    ),
  };

  const IMAGES = {
    ...DEFAULT_IMAGES,
    sizeLandscape: clamp(
      toNumber(imageConfig.sizeLandscape, DEFAULT_IMAGES.sizeLandscape),
      80,
      320,
    ),
    sizePortrait: clamp(
      toNumber(imageConfig.sizePortrait, DEFAULT_IMAGES.sizePortrait),
      60,
      260,
    ),
    cornerRadius: clamp(
      toNumber(imageConfig.cornerRadius, DEFAULT_IMAGES.cornerRadius),
      0,
      64,
    ),
  };

  function orbitalSketch(p) {
    const rawImgs = [];
    const tileImgs = [];
    const points = [];
    const hoverScales = [];
    const revealAlphas = [];
    const projected = [];
    const tileProductUrls = [];

    let sequenceStartTime = -1;
    let startSequenceDone = false;

    let canvasElement = null;
    let currentCursor = '';
    let frontmostHoveredImageIndex = -1;
    let hoverBrakeFactor = 1;
    let isMouseOverCanvas = false;

    let radius = 0;
    let angleY = 0;
    let velocityY = 0;
    let baseAutoSpeed = 0;

    let isDragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let angleYOnPress = 0;
    let lastPointerX = 0;
    let lastPointerY = 0;
    let lastPointerTime = 0;

    let touchDirectionLock = null; // null | 'orbit' | 'scroll'
    let touchStartScreenX = 0;
    let touchStartScreenY = 0;

    let diagonalRotation = 0;
    let targetRotation = 0;
    let orbitRadiusX = 0;
    let orbitRadiusY = 0;
    let imageSize = 0;
    let orientationBlend = 0;

    function getCanvasDimensions() {
      return {
        width: p.max(1, $canvas.offsetWidth || $canvas.clientWidth || 1),
        height: p.max(1, $canvas.offsetHeight || $canvas.clientHeight || 1),
      };
    }

    function computeLayout() {
      radius = p.min(p.width, p.height) * ORBIT.radiusScale;
      const shortSide = p.min(p.width, p.height);
      const longSide = p.max(p.width, p.height);
      const shortSideBlend = p.constrain(
        p.map(
          shortSide,
          RESPONSIVE.shortSideMin,
          RESPONSIVE.shortSideMax,
          0,
          1,
        ),
        0,
        1,
      );
      const stretchDamp = p.lerp(RESPONSIVE.stretchDampMin, 1, shortSideBlend);

      const aspect = p.width / p.height;
      const portraitBlend = p.constrain(
        p.map(
          aspect,
          RESPONSIVE.portraitAspect,
          RESPONSIVE.landscapeAspect,
          1,
          0,
        ),
        0,
        1,
      );

      imageSize = p.lerp(
        IMAGES.sizeLandscape,
        IMAGES.sizePortrait,
        portraitBlend,
      );
      const maxImageSizeByViewport = p.max(
        RESPONSIVE.imageMin,
        shortSide * RESPONSIVE.imageMaxRatio,
      );
      imageSize = p.constrain(
        imageSize,
        RESPONSIVE.imageMin,
        maxImageSizeByViewport,
      );

      radius +=
        longSide *
        ORBIT.radiusScale *
        RESPONSIVE.portraitRadiusBlend *
        portraitBlend;

      const effectiveStretchWide = 1 + (ORBIT.stretchWide - 1) * stretchDamp;
      const effectiveStretchNarrow =
        1 - (1 - ORBIT.stretchNarrow) * stretchDamp;

      targetRotation = p.lerp(
        0,
        p.radians(90 - ORBIT.maxTiltDegrees),
        portraitBlend,
      );
      orbitRadiusX = p.lerp(
        radius * effectiveStretchWide,
        radius * effectiveStretchNarrow,
        portraitBlend,
      );
      orbitRadiusY = p.lerp(
        radius * effectiveStretchNarrow,
        radius * effectiveStretchWide,
        portraitBlend,
      );
      orbitRadiusX *= ORBIT.spacingScale;
      orbitRadiusY *= ORBIT.spacingScale;

      const depthScaleMaxForLayout = DEPTH.enabled ? DEPTH.scaleMax : 1;
      const effectiveEdgePadding =
        ORBIT.edgePadding * p.lerp(0.5, 1, shortSideBlend);
      const imageHalfMax =
        (imageSize * depthScaleMaxForLayout * HOVER.scale) / 2 +
        effectiveEdgePadding;
      const cosR = p.abs(p.cos(targetRotation));
      const sinR = p.abs(p.sin(targetRotation));
      const projectedHalfWidth = p.sqrt(
        p.pow(orbitRadiusX * cosR, 2) + p.pow(orbitRadiusY * sinR, 2),
      );
      const projectedHalfHeight = p.sqrt(
        p.pow(orbitRadiusX * sinR, 2) + p.pow(orbitRadiusY * cosR, 2),
      );

      const availableHalfWidth = p.max(1, p.width / 2 - imageHalfMax);
      const availableHalfHeight = p.max(1, p.height / 2 - imageHalfMax);
      const widthOverflow = p.lerp(RESPONSIVE.widthOverflow, 1, shortSideBlend);
      const fitScaleX =
        (availableHalfWidth * widthOverflow) / p.max(1, projectedHalfWidth);
      const fitScaleY = availableHalfHeight / p.max(1, projectedHalfHeight);
      const fitScale = p.min(1, fitScaleX, fitScaleY);

      orbitRadiusX *= fitScale;
      orbitRadiusY *= fitScale;

      orbitRadiusX = p.max(1, orbitRadiusX);
      orbitRadiusY = p.max(1, orbitRadiusY);
      orientationBlend = portraitBlend;
    }

    function createRoundedTile(img) {
      const safeTileSize = p.max(1, p.round(imageSize));
      const pg = p.createGraphics(safeTileSize, safeTileSize);
      pg.pixelDensity(Math.min(4, Math.max(IMAGES.pixelDensity, Math.ceil(p.pixelDensity() * (DEPTH.enabled ? DEPTH.scaleMax : 1) * HOVER.scale))));
      pg.clear();

      pg.fill(DEPTH.brightnessMax);
      pg.noStroke();
      pg.rect(
        0,
        0,
        safeTileSize,
        safeTileSize,
        p.min(IMAGES.cornerRadius, safeTileSize / 2),
      );

      if (!img || !img.width || !img.height) return pg;

      pg.drawingContext.globalCompositeOperation = 'source-in';

      const scale = p.max(safeTileSize / img.width, safeTileSize / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      pg.image(img, (safeTileSize - w) / 2, (safeTileSize - h) / 2, w, h);

      pg.drawingContext.globalCompositeOperation = 'source-over';
      return pg;
    }

    function projectPoints() {
      const safeOrbitRadiusX = p.max(1, orbitRadiusX);
      const safeOrbitRadiusY = p.max(1, orbitRadiusY);

      while (projected.length < points.length) {
        projected.push({ x: 0, y: 0, depth: 0, imageIndex: 0, pointIndex: 0 });
      }

      for (let i = 0; i < points.length; i++) {
        const a = points[i].angle + angleY;
        const x = p.cos(a) * safeOrbitRadiusX;
        const y = p.sin(a) * safeOrbitRadiusY;

        const verticalDepth = y / safeOrbitRadiusY;
        const horizontalDepth = x / safeOrbitRadiusX;

        projected[i].x = x;
        projected[i].y = y;
        projected[i].depth = p.lerp(
          verticalDepth,
          horizontalDepth,
          orientationBlend,
        );
        projected[i].imageIndex = points[i].imageIndex;
        projected[i].pointIndex = i;
      }

      projected.length = points.length;
      projected.sort((a, b) => a.depth - b.depth);
      return projected;
    }

    function rebuildTiles() {
      for (let i = 0; i < tileImgs.length; i++) {
        if (tileImgs[i] && typeof tileImgs[i].remove === 'function') {
          tileImgs[i].remove();
        }
      }
      tileImgs.length = 0;
      tileProductUrls.length = 0;
      for (let i = 0; i < rawImgs.length; i++) {
        if (rawImgs[i]) {
          tileImgs[i] = createRoundedTile(rawImgs[i]);
          tileProductUrls[i] = productUrls[i] || null;
        }
      }
    }

    function toLocalOrbitSpace(screenX, screenY) {
      const mx = screenX - p.width / 2;
      const my = screenY - p.height / 2;
      const cosR = p.cos(-diagonalRotation);
      const sinR = p.sin(-diagonalRotation);
      return {
        x: mx * cosR - my * sinR,
        y: mx * sinR + my * cosR,
      };
    }

    function findFrontmostHit(screenX, screenY) {
      if (points.length === 0 || tileImgs.length === 0) return -1;
      const local = toLocalOrbitSpace(screenX, screenY);
      for (let i = projected.length - 1; i >= 0; i--) {
        const pt = projected[i];
        const sizeFactor = DEPTH.enabled
          ? p.map(pt.depth, -1, 1, DEPTH.scaleMin, DEPTH.scaleMax)
          : 1;
        const halfSize = (imageSize * sizeFactor) / 2;
        if (
          p.abs(local.x - pt.x) < halfSize &&
          p.abs(local.y - pt.y) < halfSize
        ) {
          return pt.imageIndex;
        }
      }
      return -1;
    }

    function handleOrbitClick(screenX, screenY) {
      if (!hasAnyProductUrl) return false;
      const hitIdx = findFrontmostHit(screenX, screenY);
      if (hitIdx < 0) return false;
      const url = tileProductUrls[hitIdx];
      if (!url) return false;
      baseAutoSpeed = 0;
      velocityY = 0;
      window.location.href = url;
      return true;
    }

    function renderImage(pt, localMouse) {
      if (!tileImgs[pt.imageIndex]) return;

      const sizeFactor = DEPTH.enabled
        ? p.map(pt.depth, -1, 1, DEPTH.scaleMin, DEPTH.scaleMax)
        : 1;
      const baseSize = imageSize * sizeFactor;

      const isHovered =
        p.abs(localMouse.x - pt.x) < baseSize / 2 &&
        p.abs(localMouse.y - pt.y) < baseSize / 2 &&
        !isDragging;
      if (isHovered) frontmostHoveredImageIndex = pt.imageIndex;

      const targetScale = isHovered ? HOVER.scale : 1;
      hoverScales[pt.pointIndex] = p.lerp(
        hoverScales[pt.pointIndex],
        targetScale,
        HOVER.lerpSpeed,
      );

      const drawSize = baseSize * hoverScales[pt.pointIndex];

      let depthNormalized = (pt.depth + 1) / 2;
      depthNormalized = p.pow(p.constrain(depthNormalized, 0, 1), DEPTH.gamma);
      let brightness = p.lerp(
        DEPTH.brightnessMin,
        DEPTH.brightnessMax,
        depthNormalized,
      );
      if (isHovered)
        brightness = p.min(
          brightness + HOVER.brightnessBoost,
          DEPTH.brightnessMax,
        );

      const alpha = startSequenceDone ? 255 : revealAlphas[pt.pointIndex];
      p.tint(brightness, alpha);
      p.push();
      p.translate(pt.x, pt.y);
      p.rotate(-diagonalRotation);
      p.image(tileImgs[pt.imageIndex], 0, 0, drawSize, drawSize);
      p.pop();
    }

    function beginDrag(x, y) {
      isDragging = true;
      dragStartX = x;
      dragStartY = y;
      angleYOnPress = angleY;
      lastPointerX = x;
      lastPointerY = y;
      lastPointerTime = p.millis();
      velocityY = 0;
    }

    function updateDrag(x, y) {
      const dx = x - dragStartX;
      const dy = y - dragStartY;
      const combinedDrag = -(
        dx * INTERACTION.dragHorizontalWeight +
        dy * INTERACTION.dragVerticalWeight
      );

      angleY = angleYOnPress + combinedDrag * INTERACTION.dragSensitivity;

      const now = p.millis();
      let dt = now - lastPointerTime;
      if (dt <= 0) dt = INTERACTION.minFrameMs;

      const instantDX = x - lastPointerX;
      const instantDY = y - lastPointerY;
      const instantCombined = -(
        instantDX * INTERACTION.dragHorizontalWeight +
        instantDY * INTERACTION.dragVerticalWeight
      );

      velocityY = (instantCombined * INTERACTION.flingSpeed) / dt;

      lastPointerX = x;
      lastPointerY = y;
      lastPointerTime = now;
    }

    function endDrag() {
      isDragging = false;
    }

    function handleTouchCancel(event) {
      if (touchDirectionLock === 'orbit') endDrag();
      touchDirectionLock = null;
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
    }

    function initializePoints() {
      points.length = 0;
      hoverScales.length = 0;
      revealAlphas.length = 0;

      const totalImages = Math.max(1, imageUrls.length);
      for (let i = 0; i < ORBIT.imageCount; i++) {
        points.push({
          angle: p.map(i, 0, ORBIT.imageCount, 0, p.TWO_PI),
          imageIndex: i % totalImages,
        });
        hoverScales.push(1);
        revealAlphas.push(0);
      }
    }

    function advanceRevealSequence() {
      if (startSequenceDone || sequenceStartTime < 0) return;
      const now = p.millis();
      let allDone = true;
      for (let i = 0; i < revealAlphas.length; i++) {
        const elapsed = now - sequenceStartTime - i * REVEAL.staggerMs;
        const t = clamp(elapsed / REVEAL.fadeMs, 0, 1);
        revealAlphas[i] = t * 255;
        if (t < 1) allDone = false;
      }
      if (allDone) startSequenceDone = true;
    }

    function loadImagesThenStart() {
      let loopStarted = false;
      baseAutoSpeed = p.TWO_PI / ORBIT.revolutionMs;
      initializePoints();

      function onImageLoaded(index, img) {
        rawImgs[index] = img;
        tileImgs[index] = createRoundedTile(img);
        tileProductUrls[index] = productUrls[index] || null;

        if (!loopStarted) {
          loopStarted = true;
          sequenceStartTime = p.millis();
          startSequenceDone = false;
          p.loop();
        }
      }

      function remapFailedPoints(failedIndex) {
        const loadedIndices = [];
        for (let i = 0; i < tileImgs.length; i++) {
          if (tileImgs[i]) loadedIndices.push(i);
        }
        if (loadedIndices.length === 0) return;
        for (let i = 0; i < points.length; i++) {
          if (points[i].imageIndex === failedIndex) {
            points[i].imageIndex = loadedIndices[i % loadedIndices.length];
          }
        }
      }

      function onImageFailed(index) {
        console.warn(
          '[orbital-p5-sketch] Failed to load image:',
          imageUrls[index],
        );
        remapFailedPoints(index);
      }

      for (let i = 0; i < imageUrls.length; i++) {
        p.loadImage(
          imageUrls[i],
          (function makeOnLoad(index) {
            return function handleLoad(img) {
              onImageLoaded(index, img);
            };
          })(i),
          (function makeOnError(index) {
            return function handleError() {
              onImageFailed(index);
            };
          })(i),
        );
      }
    }

    p.setup = function setupCanvas() {
      const { width, height } = getCanvasDimensions();
      const $createdCanvas = p.createCanvas(width, height);
      $createdCanvas.style('display', 'block');
      canvasElement = $createdCanvas.elt;
      $createdCanvas.mouseOver(function onMouseOver() {
        isMouseOverCanvas = true;
      });
      $createdCanvas.mouseOut(function onMouseOut() {
        isMouseOverCanvas = false;
        endDrag();
      });
      if (canvasElement) {
        canvasElement.addEventListener('touchcancel', handleTouchCancel, {
          passive: false,
        });
      }
      p.imageMode(p.CENTER);

      computeLayout();
      p.noLoop();
      loadImagesThenStart();
    };

    p.windowResized = function onResize() {
      const { width, height } = getCanvasDimensions();
      p.resizeCanvas(width, height);
      computeLayout();
      if (rawImgs.length === 0) return;
      rebuildTiles();
      if (tileImgs.length === 0) return;
      if (points.length === 0) initializePoints();
    };

    p.draw = function renderFrame() {
      diagonalRotation = p.lerp(
        diagonalRotation,
        targetRotation,
        INTERACTION.rotationLerpSpeed,
      );

      p.background(0);
      p.translate(p.width / 2, p.height / 2);

      if (!isDragging) {
        const isAnyImageHovered =
          isMouseOverCanvas && frontmostHoveredImageIndex >= 0;
        const brakeTarget = isAnyImageHovered ? 0 : 1;
        hoverBrakeFactor = p.lerp(
          hoverBrakeFactor,
          brakeTarget,
          HOVER.brakeLerpSpeed,
        );

        angleY += baseAutoSpeed * hoverBrakeFactor * p.deltaTime;
        angleY += velocityY * p.deltaTime;
        velocityY *= INTERACTION.velocityFriction;
        if (p.abs(velocityY) < INTERACTION.velocityThreshold) velocityY = 0;
      } else {
        hoverBrakeFactor = 1;
      }

      if (points.length === 0 || tileImgs.length === 0) return;

      advanceRevealSequence();

      const projected = projectPoints();
      const localMouse = toLocalOrbitSpace(p.mouseX, p.mouseY);
      frontmostHoveredImageIndex = -1;

      p.push();
      p.rotate(diagonalRotation);

      for (let i = 0; i < projected.length; i++) {
        renderImage(projected[i], localMouse);
      }

      p.pop();

      if (hasAnyProductUrl) {
        const hitIdx = frontmostHoveredImageIndex;
        const wantCursor =
          !isDragging && hitIdx >= 0 && tileProductUrls[hitIdx]
            ? 'pointer'
            : '';
        if (wantCursor !== currentCursor) {
          currentCursor = wantCursor;
          if (canvasElement) canvasElement.style.cursor = currentCursor;
        }
      }
    };

    p.mousePressed = function onMouseDown() {
      beginDrag(p.mouseX, p.mouseY);
    };
    p.mouseDragged = function onMouseDrag() {
      updateDrag(p.mouseX, p.mouseY);
    };
    p.mouseReleased = function onMouseUp() {
      const displacement = p.dist(dragStartX, dragStartY, p.mouseX, p.mouseY);
      endDrag();
      if (
        p.mouseButton === p.LEFT &&
        displacement < INTERACTION.clickMaxDisplacement
      ) {
        handleOrbitClick(p.mouseX, p.mouseY);
      }
    };

    p.touchStarted = function onTouchStart() {
      if (p.touches.length > 0) {
        touchStartScreenX = p.touches[0].x;
        touchStartScreenY = p.touches[0].y;
        touchDirectionLock = null;
      }
    };
    p.touchMoved = function onTouchMove() {
      if (p.touches.length === 0) return;
      const tx = p.touches[0].x;
      const ty = p.touches[0].y;

      if (touchDirectionLock === null) {
        const adx = Math.abs(tx - touchStartScreenX);
        const ady = Math.abs(ty - touchStartScreenY);
        if (
          adx < INTERACTION.touchLockThreshold &&
          ady < INTERACTION.touchLockThreshold
        ) {
          return; // not enough movement to decide direction yet
        }
        if (adx >= ady) {
          touchDirectionLock = 'orbit';
          beginDrag(tx, ty);
        } else {
          touchDirectionLock = 'scroll';
        }
      }

      if (touchDirectionLock === 'orbit') {
        updateDrag(tx, ty);
        return false; // prevent page scroll
      }
      // 'scroll' — browser handles scrolling normally
    };
    p.touchEnded = function onTouchEnd() {
      if (touchDirectionLock === null) {
        if (handleOrbitClick(touchStartScreenX, touchStartScreenY)) {
          return false;
        }
        return;
      }
      const wasOrbit = touchDirectionLock === 'orbit';
      if (wasOrbit) endDrag();
      touchDirectionLock = null;
      if (wasOrbit) return false;
    };

    function handleVisibilityChange() {
      if (document.hidden) endDrag();
    }

    function handlePageShow(event) {
      if (event.persisted) {
        isDragging = false;
        baseAutoSpeed = p.TWO_PI / ORBIT.revolutionMs;
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    p.remove = (function wrapRemove(originalRemove) {
      return function removeWithCleanup() {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange,
        );
        window.removeEventListener('pageshow', handlePageShow);
        if (canvasElement) {
          canvasElement.removeEventListener('touchcancel', handleTouchCancel);
          canvasElement = null;
        }
        return originalRemove.call(this);
      };
    })(p.remove);
  }

  let orbitalInstance = new p5(orbitalSketch, $canvas);

  function isMatchingUnloadEvent(event) {
    if (!event) return false;
    const detailSectionId = event.detail && event.detail.sectionId;
    if (detailSectionId) return detailSectionId === sectionId;
    const eventTarget = event.target;
    return eventTarget === $section || $section.contains(eventTarget);
  }

  function teardown() {
    if (!orbitalInstance) return;
    orbitalInstance.remove();
    orbitalInstance = null;
    document.removeEventListener('shopify:section:unload', onSectionUnload);
    document.removeEventListener('shopify:block:unload', onBlockUnload);
  }

  function onSectionUnload(event) {
    if (!isMatchingUnloadEvent(event)) return;
    teardown();
  }

  function onBlockUnload(event) {
    if (!isMatchingUnloadEvent(event)) return;
    teardown();
  }

  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:block:unload', onBlockUnload);
})();
